// Every href the library page builds, in one place.
//
// The API deliberately returns facts (`see_all_kind`, `resume.kind`) and never
// URLs — the server does not own frontend routes. This module is the only place
// that turns those facts into paths.

import type {
  BrowseSort,
  IllustratedFilter,
  LengthBand,
  LibraryTab,
  ShelfBook,
} from "./library-types";

/** The book's jacket page. */
export function bookHref(book: Pick<ShelfBook, "id">): string {
  return `/books/${book.id}`;
}

/**
 * Where "Read now" goes for a book nobody has opened yet. The page-image reader
 * (`/read`) is the right target when a book has rendered pages; otherwise the
 * reflowable chapter reader. We can't tell from a shelf card, so we send
 * everyone to the jacket's own reader entry, which already knows.
 */
export function readHref(book: Pick<ShelfBook, "id">): string {
  return `/books/${book.id}/read`;
}

// Where "Continue" goes lives in `progress-api.ts`, next to the client that
// fetches the pointer — it is a reader concern, not a library one, and both the
// book jacket and the library's Continue-reading rail need it. Re-exported here
// so library components have one import for all their hrefs.
export { resumeHref } from "./progress-api";

export interface BrowseQuery {
  kind?: string | null;
  lengths?: LengthBand[];
  illustrated?: IllustratedFilter[];
  sort?: BrowseSort;
}

/** A deep link into the Browse-all tab with filters pre-applied. */
export function browseHref(query: BrowseQuery = {}): string {
  const params = new URLSearchParams({ tab: "browse" });
  if (query.kind) params.set("kind", query.kind);
  if (query.lengths?.length) params.set("length", query.lengths.join(","));
  // Omitted when both boxes are ticked — that's the same set as no filter.
  if (query.illustrated?.length === 1) {
    params.set("plates", query.illustrated[0] === "plates" ? "yes" : "no");
  }
  if (query.sort && query.sort !== "newest") params.set("sort", query.sort);
  return `/library?${params.toString()}`;
}

/** A link to another library tab, preserving nothing else. */
export function tabHref(tab: LibraryTab): string {
  return tab === "reading" ? "/library" : `/library?tab=${tab}`;
}

/** A collection's own section on the Collections tab. */
export function collectionHref(slug: string): string {
  return `/library?tab=collections&c=${encodeURIComponent(slug)}`;
}
